import {
  DEFAULT_HOME_LOOK,
  type CatalogResponse,
  type CatalogShop,
  type CivicInfo,
  type PlacementPresentation,
  type PropertyPublic,
  type RealEstateListingsResponse,
  type WorldManifest,
} from "@townsy/contracts";
import { getInterior, getPlot } from "@townsy/world";
import { HOME_STAGE_LABELS, formatCents } from "../lib/format";
import { formatCoins } from "../lib/wallet";
import type { InteriorBoardView, InteriorView, ListingLine } from "../scene/types";

/**
 * Builds the renderer's `InteriorView` for the one shared room of a property (pure; unit-tested):
 *  - stores: business sign + the owner's TV board (`PropertyPublic.interiorBoard`, plain text);
 *  - the Real Estate office: its name + the listings board (prices pre-formatted from the DTO);
 *  - Home Builders / Furnishings showrooms: their name + catalog highlights ("Sofa · 120 coins");
 *  - homes: stage + address on the wall, the owner's free look and the furniture placed inside.
 */
export type CivicKind = CivicInfo["kind"];

export function civicKindOf(manifest: WorldManifest, propertyId: string): CivicKind | null {
  return getPlot(manifest, propertyId)?.civic?.kind ?? null;
}

export function findCivicId(manifest: WorldManifest, kind: CivicKind): string | null {
  return manifest.plots.find((p) => p.civic?.kind === kind)?.id ?? null;
}

export function isRealEstateOfficePlot(manifest: WorldManifest, propertyId: string): boolean {
  return civicKindOf(manifest, propertyId) === "real_estate_office";
}

export function findRealEstateOfficeId(manifest: WorldManifest): string | null {
  return findCivicId(manifest, "real_estate_office");
}

/** The shop a civic showroom sells from (null for anything else). */
export function showroomShop(kind: CivicKind | null): CatalogShop | null {
  return kind === "home_builders" ? "home_builders" : kind === "furnishings" ? "furnishings" : null;
}

export function listingLines(listings: Pick<RealEstateListingsResponse, "items"> | null | undefined): ListingLine[] {
  if (!listings) return [];
  return listings.items.map((l) => ({
    address: l.address,
    priceLabel: l.offer.coins !== null ? formatCoins(l.offer.coins) : l.offer.usdCents !== null ? formatCents(l.offer.usdCents) : "—",
  }));
}

/** Lines on a showroom's board (pre-formatted from the catalog DTO; prices are never computed here). */
export const SHOWROOM_MAX_LINES = 8;

export function showroomLines(catalog: Pick<CatalogResponse, "items" | "stages"> | null | undefined, shop: CatalogShop, max = SHOWROOM_MAX_LINES): ListingLine[] {
  if (!catalog) return [];
  const price = (coins: number | null, usdCents: number | null) => (coins !== null ? formatCoins(coins) : usdCents !== null ? formatCents(usdCents) : "—");
  const lines: ListingLine[] = [];
  if (shop === "home_builders") {
    // The next level is what most visitors come for: the upgrades lead the board.
    for (const s of catalog.stages) {
      if (!s.offer) continue;
      lines.push({ address: `${s.name} upgrade`, priceLabel: price(s.offer.coins, s.offer.usdCents) });
    }
  }
  for (const item of catalog.items) {
    if (item.shop !== shop) continue;
    lines.push({ address: item.name, priceLabel: price(item.offer.coins, item.offer.usdCents) });
  }
  return lines.slice(0, max);
}

export function boardView(property: Pick<PropertyPublic, "interiorBoard"> | null | undefined): InteriorBoardView | null {
  const b = property?.interiorBoard;
  return b ? { title: b.title, body: b.body } : null;
}

/** A home's interior as the renderer needs it: stage, free look, and the furniture placed inside. */
export function homeView(property: Pick<PropertyPublic, "home"> | null | undefined): InteriorView["home"] {
  const home = property?.home;
  if (!home) return null;
  return {
    stage: home.stage,
    look: { ...DEFAULT_HOME_LOOK, ...(home.look ?? {}) },
    placedItems: home.placedItems.filter((p) => p.scope === "interior"),
  };
}

export function buildInteriorView(
  manifest: WorldManifest,
  propertyId: string,
  property: PropertyPublic | null | undefined,
  listings: Pick<RealEstateListingsResponse, "items"> | null | undefined,
  catalog?: Pick<CatalogResponse, "items" | "stages"> | null,
): InteriorView | null {
  const plot = getPlot(manifest, propertyId);
  if (!plot) return null;
  const template = getInterior(manifest, plot.interiorTemplate);
  const office = plot.civic?.kind === "real_estate_office";
  const shop = showroomShop(plot.civic?.kind ?? null);
  let wallLabel: string;
  if (plot.type === "CIVIC") wallLabel = property?.civic?.name ?? plot.civic?.name ?? plot.address;
  else if (plot.type === "COMMERCIAL") {
    // No brand yet: say so on the wall (the renderer derives the monogram from this label, so a bare address read "1M").
    wallLabel = property?.placement?.businessName ?? `${property?.status === "OWNED" ? "Opening Soon" : "Available Shop"} · ${plot.address}`;
  }
  else wallLabel = property?.home ? `${HOME_STAGE_LABELS[property.home.stage] ?? "Home"} · ${plot.address}` : plot.address;
  return {
    propertyId,
    template,
    presentation: plot.type === "COMMERCIAL" ? (property?.placement ?? null) : null,
    wallLabel,
    board: plot.type === "COMMERCIAL" ? boardView(property) : null,
    storeTheme: plot.type === "COMMERCIAL" ? (property?.interiorTheme ?? "general") : null,
    listings: office ? listingLines(listings) : shop ? showroomLines(catalog ?? null, shop) : null,
    home: plot.type === "RESIDENTIAL" ? homeView(property) : null,
  };
}

/**
 * What the room's branding actually renders (sign text, mark, colours, logo). `presentationVersion`
 * is deliberately NOT part of it: the API bumps it on every store-board edit too (so realtime rooms
 * re-announce the property), and a board edit must update the TV in place, not rebuild the room.
 */
export function placementBrandingKey(p: Pick<PlacementPresentation, "placementId" | "businessName" | "monogram" | "brandColor" | "accentColor" | "logoTextureUrl"> | null | undefined): string {
  return p ? [p.placementId, p.businessName, p.monogram, p.brandColor, p.accentColor, p.logoTextureUrl ?? ""].join("|") : "";
}

/** Changes that need the room rebuilt (template, branding, wall text). A home's look/furniture never does. */
export function interiorRoomKey(view: InteriorView): string {
  return [view.propertyId, view.template.id, placementBrandingKey(view.presentation), view.wallLabel].join("|");
}

/** Changes that only need `updateInteriorBoard` (TV text, listings lines). */
export function interiorBoardKey(view: Pick<InteriorView, "board" | "listings">): string {
  return JSON.stringify([view.board, view.listings]);
}

/** Changes that only need `updateInteriorHome` (look, furniture). */
export function interiorHomeKey(view: Pick<InteriorView, "home">): string {
  return JSON.stringify(view.home);
}

/**
 * Changes that only need `updateInteriorStoreTheme` (the owner picked another store theme; ADR-0006).
 * The theme is not part of the room key: everyone inside sees the room change in place.
 */
export function interiorThemeKey(view: Pick<InteriorView, "storeTheme">): string {
  return view.storeTheme ?? "";
}
